import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Send, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  AlertCircle
} from 'lucide-react';
import { DashboardStats } from '@/types/dashboard';

interface DisparosStatsCardsProps {
  stats: DashboardStats;
  onCardClick?: (metricType: 'pendentes' | 'enviados' | 'invalidos' | 'falhas' | 'baixados') => void;
}

const DisparosStatsCards: React.FC<DisparosStatsCardsProps> = ({ stats, onCardClick }) => {
  // Função para formatar valores monetários em formato brasileiro
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Função para formatar números com separadores
  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value || 0);
  };

  // Verificar se stats é válido
  if (!stats || typeof stats !== 'object') {
    console.log('⚠️ [DisparosStatsCards] Stats inválidos:', stats);
    return (
      <div className="space-y-6">
        <div className="text-center py-8 text-gray-500">
          <p>Dados não disponíveis</p>
        </div>
      </div>
    );
  }

  // Garantir que os arrays existam e sejam válidos
  const statusAgrupados = stats.status_agrupados || {
    pendentes: 0,
    enviados: 0,
    invalidos: 0,
    falhas: 0,
    baixados: 0
  };
  
  const tiposEnvio = stats.tipos_envio || [];
  const totalEnvios = stats.total_envios || 0;

  // Calcular totais por tipo
  const totalPorTipo = tiposEnvio.reduce((acc, tipo) => {
    acc[tipo.tipo] = {
      quantidade: tipo.quantidade || 0,
      valor: tipo.valor_total || 0
    };
    return acc;
  }, {} as Record<string, { quantidade: number; valor: number }>);

  return (
    <div className="space-y-6">
      {/* Cards de Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card 
          className="glass-card hover-scale cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-yellow-300"
          onClick={() => onCardClick?.('pendentes')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Pendentes</CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {formatNumber(statusAgrupados.pendentes)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Aguardando envio
            </p>
          </CardContent>
        </Card>

        <Card 
          className="glass-card hover-scale cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-green-300"
          onClick={() => onCardClick?.('enviados')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Enviados</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatNumber(statusAgrupados.enviados)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Mensagens enviadas
            </p>
          </CardContent>
        </Card>

        <Card 
          className="glass-card hover-scale cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-orange-300"
          onClick={() => onCardClick?.('invalidos')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Inválidos</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatNumber(statusAgrupados.invalidos)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Contatos inválidos
            </p>
          </CardContent>
        </Card>

        <Card 
          className="glass-card hover-scale cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-red-300"
          onClick={() => onCardClick?.('falhas')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Falhas</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatNumber(statusAgrupados.falhas || 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Erros de envio
            </p>
          </CardContent>
        </Card>

        <Card 
          className="glass-card hover-scale cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-blue-300"
          onClick={() => onCardClick?.('baixados')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Baixados</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {formatNumber(statusAgrupados.baixados)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Boletos pagos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Cards por Tipo de Envio */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quantidade por Tipo */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary-600" />
              Quantidade por Tipo de Envio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {tiposEnvio.map((tipo) => (
              <div key={tipo.tipo} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="capitalize">
                    {tipo.tipo.toLowerCase()}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    {tipo.quantidade} envios
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatNumber(tipo.quantidade)}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Valor por Tipo */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Valor Total por Tipo de Envio
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {tiposEnvio.map((tipo) => (
              <div key={tipo.tipo} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="capitalize">
                    {tipo.tipo.toLowerCase()}
                  </Badge>
                  <span className="text-sm text-gray-600">
                    {formatCurrency(tipo.valor_total)}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(tipo.valor_total)}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Resumo Geral */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-purple-600" />
            Resumo Geral do Período
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600">
                {formatNumber(totalEnvios)}
              </div>
              <p className="text-sm text-gray-600 mt-1">Total de Envios</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {formatCurrency(tiposEnvio.reduce((sum, tipo) => sum + (tipo.valor_total || 0), 0))}
              </div>
              <p className="text-sm text-gray-600 mt-1">Valor Total</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {tiposEnvio.length}
              </div>
              <p className="text-sm text-gray-600 mt-1">Tipos de Envio</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DisparosStatsCards;
